import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {Dropdown, Duration, InsuranceCoverage, Sex, Version} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TableRowSelectEvent} from 'primeng/table';
import {merge} from 'rxjs';
import {Product} from '../coverage-builder-section.component';

interface CoverageSelected {
  coverageAmount: FormControl<number | null | undefined>;
  duration: FormControl<Duration | null | undefined>;
}

@UntilDestroy()
@Component({
  template: '',
})
export abstract class BaseLifeInsuranceCardComponent {
  @Input() set ratesVersion(ratesVersion: Version) {
    this._ratesVersion = ratesVersion;
    this.setProducts();
  }

  @Input() set smoker(_smoker: boolean) {
    this._smoker = _smoker;
    this.setProducts();
  }

  @Input() set sex(_sex: Sex) {
    this._sex = _sex;
    this.setProducts();
  }

  @Input() set age(_age: number) {
    this._age = _age;
    this.setProducts();
  }

  @Input({required: true}) set selectedWholeLifeCoverageAmount(_: number | undefined) {
    this.setProducts();
  }

  @Input({required: true}) set selectedTermLifeCoverageAmount(_: number | undefined) {
    this.setProducts();
  }

  @Input() set selectedDuration(duration: Duration | undefined) {
    this.form.controls.duration.setValue(duration, {emitEvent: false});
    this.setProducts();
  }

  @Input() withProductSelection = true;

  @Output() coverageSelected = new EventEmitter<InsuranceCoverage>();

  form!: FormGroup<CoverageSelected>;
  _ratesVersion: Version | undefined;
  _age: number | undefined;
  _smoker: boolean | undefined;
  _sex: Sex | undefined;
  isPriceLoading = false;
  products: Product[] = [];
  coverageAmountOptions: Dropdown<number | undefined>[] = [];
  hasDuo: boolean | undefined;
  selectedProductIndex = 0;

  protected constructor(
    protected readonly fb: FormBuilder,
    defaultDuration: Duration,
  ) {
    this.form = this.createForm(defaultDuration);
  }

  onSelect($event: TableRowSelectEvent): void {
    this.coverageSelected.emit({
      amount: this.form.controls.coverageAmount.value ?? undefined,
      duration: $event.data.value,
    });
  }

  private createForm(defaultDuration: Duration): FormGroup<CoverageSelected> {
    const form = this.fb.group<CoverageSelected>({
      coverageAmount: this.fb.control(null),
      duration: this.fb.control(null),
    });

    form.controls.coverageAmount.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.isPriceLoading = true;
    });

    merge(form.controls.coverageAmount.valueChanges, form.controls.duration.valueChanges)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const {coverageAmount, duration} = form.getRawValue();
        this.coverageSelected.emit({
          amount: coverageAmount ?? undefined,
          duration: duration ?? defaultDuration,
        });
      });

    return form;
  }

  protected abstract setProducts(): void;
}
